import { useState, useRef, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useCountUp } from '../hooks/useCountUp';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);

const getSliderBg = (value: number, min: number, max: number) => {
  const pct = ((value - min) / (max - min)) * 100;
  return `linear-gradient(to right, #E94560 0%, #E94560 ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`;
};

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  unitLeft?: string;
  onChange: (v: number) => void;
  formatDisplay?: (v: number) => string;
}

function SliderField({
  label, value, min, max, step, unit, unitLeft, onChange, formatDisplay,
}: SliderFieldProps) {
  const display = formatDisplay ? formatDisplay(value) : String(value);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-b2bbody text-sm font-semibold text-gray-700">{label}</label>
        <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 min-w-[80px] justify-center">
          {unitLeft && <span className="font-b2b text-sm font-bold text-navy/60">{unitLeft}</span>}
          <span className="font-b2b text-sm font-bold text-navy">{display}</span>
          {unit && <span className="font-b2b text-sm font-bold text-navy/60">{unit}</span>}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        className="roi-slider w-full"
        style={{ background: getSliderBg(value, min, max) }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="flex justify-between mt-1">
        <span className="font-b2bbody text-[11px] text-gray-400">{min}{unit ?? ''}</span>
        <span className="font-b2bbody text-[11px] text-gray-400">{max}{unit ?? ''}</span>
      </div>
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  min: number;
  step: number;
  unitLeft?: string;
  onChange: (v: number) => void;
}

function NumberField({ label, value, min, step, unitLeft, onChange }: NumberFieldProps) {
  const [raw, setRaw] = useState(String(value));

  useEffect(() => {
    setRaw(value.toLocaleString('pt-BR'));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setRaw(digits);
    const num = Number(digits);
    if (!isNaN(num) && num >= min) onChange(num);
  };

  return (
    <div>
      <label className="font-b2bbody text-sm font-semibold text-gray-700 block mb-2">{label}</label>
      <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50 focus-within:border-coral focus-within:bg-white transition-colors">
        {unitLeft && (
          <span className="font-b2b font-bold text-navy/50 text-sm px-3 py-3 bg-gray-100 border-r border-gray-200 select-none">
            {unitLeft}
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={handleChange}
          className="flex-1 bg-transparent px-3 py-3 font-b2b font-bold text-navy text-sm outline-none w-full"
        />
      </div>
    </div>
  );
}

interface ResultMetricProps {
  label: string;
  value: number;
  color: 'red' | 'green';
  large?: boolean;
  prefix?: string;
  suffix?: string;
}

function ResultMetric({ label, value, color, large, prefix = 'R$\u00A0', suffix = '/mês' }: ResultMetricProps) {
  const animated = useCountUp(value);
  const formatted = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(animated);

  return (
    <div className={`flex flex-col ${large ? 'gap-0.5' : 'gap-0.5'}`}>
      <p className="font-b2bbody text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
      <div className="flex items-baseline gap-1 flex-wrap">
        <span
          className={`font-b2b font-extrabold leading-none ${
            color === 'red' ? 'text-red-500' : 'text-green-500'
          } ${large ? 'text-3xl sm:text-4xl' : 'text-2xl'}`}
        >
          {prefix}{formatted}
        </span>
        {!large && (
          <span className={`font-b2bbody text-sm font-medium ${color === 'red' ? 'text-red-400' : 'text-green-400'}`}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

interface ROICalculatorProps {
  onCtaClick: () => void;
}

export default function ROICalculator({ onCtaClick }: ROICalculatorProps) {
  const { ref, visible } = useScrollAnimation(0.07);
  const sectionRef = useRef<HTMLElement>(null);
  const [showStickyMobile, setShowStickyMobile] = useState(false);

  const [msgsDia, setMsgsDia] = useState(100);
  const [ticketMedio, setTicketMedio] = useState(5000);
  const [taxaConversao, setTaxaConversao] = useState(5);
  const [pctNaoRespondido, setPctNaoRespondido] = useState(30);

  // Calculations
  const leadsPerdidosMes = msgsDia * 30 * (pctNaoRespondido / 100);
  const vendasPerdidasMes = leadsPerdidosMes * (taxaConversao / 100);
  const receitaPerdidasMes = Math.round(vendasPerdidasMes * ticketMedio);
  const receitaPerdidasAno = receitaPerdidasMes * 12;
  const leadsRecuperados = leadsPerdidosMes * 0.83;
  const vendasRecuperadas = leadsRecuperados * (taxaConversao / 100);
  const receitaRecuperadaMes = Math.round(vendasRecuperadas * ticketMedio);

  // Sticky mobile observer
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyMobile(entry.isIntersecting),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      {/* Slider styles */}
      <style>{`
        .roi-slider { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 3px; outline: none; cursor: pointer; }
        .roi-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 50%; background: #E94560; cursor: pointer; border: 3px solid white; box-shadow: 0 2px 8px rgba(233,69,96,0.45); }
        .roi-slider::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: #E94560; cursor: pointer; border: 3px solid white; box-shadow: 0 2px 8px rgba(233,69,96,0.45); }
      `}</style>

      <section
        id="calculator"
        ref={(el) => {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
          (sectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
        }}
        className="bg-white py-24 lg:py-32 px-6"
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-5 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
              Calculadora de ROI
            </span>
          </div>

          <div
            className={`text-center mb-4 transition-all duration-700 delay-75 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-b2b text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-navy leading-tight">
              Quanto dinheiro sua empresa está{' '}
              <span className="text-coral">deixando na mesa?</span>
            </h2>
          </div>

          <div
            className={`text-center mb-14 transition-all duration-700 delay-150 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="font-b2bbody text-gray-500 text-base sm:text-lg max-w-xl mx-auto">
              Preencha com os números do seu negócio e descubra o impacto real.
            </p>
          </div>

          {/* Main card */}
          <div
            className={`bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ boxShadow: '0 8px 48px rgba(10,22,40,0.10)' }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px]">

              {/* Left: Inputs */}
              <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-gray-100">
                <div className="flex items-center gap-2 mb-8">
                  <div className="w-8 h-8 rounded-lg bg-coral/10 flex items-center justify-center">
                    <i className="ri-equalizer-2-line text-coral text-sm" />
                  </div>
                  <h3 className="font-b2b text-base font-bold text-navy">Seu Negócio Hoje</h3>
                </div>

                <div className="space-y-8">
                  <SliderField
                    label="Mensagens recebidas por dia"
                    value={msgsDia}
                    min={10}
                    max={500}
                    step={10}
                    unit=" msgs"
                    onChange={setMsgsDia}
                  />
                  <NumberField
                    label="Ticket médio por venda"
                    value={ticketMedio}
                    min={500}
                    step={500}
                    unitLeft="R$"
                    onChange={setTicketMedio}
                  />
                  <SliderField
                    label="Taxa de conversão atual"
                    value={taxaConversao}
                    min={1}
                    max={30}
                    step={1}
                    unit="%"
                    onChange={setTaxaConversao}
                    formatDisplay={(v) => `${v}%`}
                  />
                  <SliderField
                    label="Leads que ficam sem resposta"
                    value={pctNaoRespondido}
                    min={5}
                    max={70}
                    step={5}
                    unit="%"
                    onChange={setPctNaoRespondido}
                    formatDisplay={(v) => `${v}%`}
                  />
                </div>

                {/* Assumptions note */}
                <p className="mt-8 font-b2bbody text-xs text-gray-400 leading-relaxed">
                  * Cálculo baseado em 30 dias/mês. A Automat recupera em média 83% dos leads
                  não respondidos com base em clientes ativos.
                </p>
              </div>

              {/* Right: Results */}
              <div
                className="p-8 lg:p-10 flex flex-col"
                style={{ background: 'linear-gradient(160deg, #0A1628 0%, #162D5A 100%)' }}
              >
                <div className="flex items-center gap-2 mb-8">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <i className="ri-bar-chart-2-line text-white text-sm" />
                  </div>
                  <h3 className="font-b2b text-base font-bold text-white">Seu Diagnóstico</h3>
                </div>

                {/* Losses */}
                <div className="space-y-5 mb-7">
                  <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-arrow-down-line text-red-400 text-sm" />
                      </div>
                      <div className="space-y-4">
                        <ResultMetric
                          label="Você perde por mês"
                          value={receitaPerdidasMes}
                          color="red"
                          suffix="/mês"
                        />
                        <div className="h-px bg-red-500/20" />
                        <ResultMetric
                          label="Você perde por ano"
                          value={receitaPerdidasAno}
                          color="red"
                          large
                          suffix="/ano"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-7">
                  <div className="flex-1 h-px bg-white/10" />
                  <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
                    <i className="ri-magic-line text-coral text-xs" />
                    <span className="font-b2bbody text-white/50 text-[11px] font-medium">com Automat</span>
                  </div>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Recovery */}
                <div className="bg-green-500/10 border border-green-500/25 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-arrow-up-line text-green-400 text-sm" />
                    </div>
                    <ResultMetric
                      label="Você recupera por mês"
                      value={receitaRecuperadaMes}
                      color="green"
                      large
                      suffix="/mês"
                    />
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-auto">
                  <button
                    onClick={onCtaClick}
                    className="whitespace-nowrap w-full font-b2bbody font-bold text-base px-6 py-4 rounded-xl text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 mb-3"
                    style={{ background: 'linear-gradient(135deg, #E94560, #C93050)' }}
                  >
                    <i className="ri-money-dollar-circle-line text-lg" />
                    Quero Recuperar Esse Dinheiro
                  </button>
                  <p className="text-center font-b2bbody text-white/40 text-xs leading-relaxed">
                    Agende uma demo gratuita e veja o agente funcionando para o seu negócio.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sticky bottom bar */}
      {showStickyMobile && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-3"
          style={{ background: 'linear-gradient(to top, rgba(10,22,40,0.98) 80%, transparent)' }}
        >
          <div className="flex items-center justify-between gap-3 bg-navy border border-white/10 rounded-2xl px-4 py-3">
            <div>
              <p className="font-b2bbody text-[10px] text-white/40 uppercase tracking-wider">
                Você recupera
              </p>
              <p className="font-b2b font-extrabold text-green-400 text-xl leading-none">
                {formatCurrency(receitaRecuperadaMes)}<span className="text-sm font-medium text-green-400/70">/mês</span>
              </p>
            </div>
            <button
              onClick={onCtaClick}
              className="whitespace-nowrap font-b2bbody font-bold text-sm px-5 py-2.5 rounded-xl text-white cursor-pointer flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #E94560, #C93050)' }}
            >
              Quero isso
            </button>
          </div>
        </div>
      )}
    </>
  );
}